import { z } from 'zod';
import { apiEnvelope } from '../envelope';

/**
 * Wire contracts for the member voice preference surface. The catalog itself
 * (including server-owned `voice_settings`) is API config-layer code and never
 * crosses this boundary — these schemas are `.strict()` so an accidental
 * settings leak fails response validation loudly.
 */

export const offeredVoiceSchema = z
  .object({
    voiceId: z.string().min(1),
    /** Absolute URL into the voice-assets distribution; absent until recorded clips land. */
    previewUrl: z.string().url().optional(),
    isDefault: z.boolean(),
  })
  .strict();

// Display name + description are deliberately NOT on the wire: ADR-017 makes the
// server localization catalog the single source of truth for every user-facing
// string. The voice-selection UI (separate ticket) sources labels from the
// locale catalog keyed by `voiceId`; shipping the catalog's interim English
// literals here would be a hardcoded-user-facing-string violation.

export type OfferedVoice = z.infer<typeof offeredVoiceSchema>;

export const voiceOfferDataSchema = z
  .object({
    /** The offerable subset of the catalog. */
    voices: z.array(offeredVoiceSchema).min(1),
    /**
     * The member's stored preference. May name a voice absent from `voices`
     * (a grandfathered choice that dropped out of the offer); null when unset.
     */
    selectedVoiceId: z.string().min(1).nullable(),
    /** What the member effectively gets: stored preference, else the default. */
    effectiveVoiceId: z.string().min(1),
  })
  .strict();

export type VoiceOfferData = z.infer<typeof voiceOfferDataSchema>;
export const voiceOfferResponseSchema = apiEnvelope(voiceOfferDataSchema);
export type VoiceOfferResponse = z.infer<typeof voiceOfferResponseSchema>;

/** PUT body — member identity comes from the bearer token, never the body. */
export const voicePreferencePutRequestSchema = z
  .object({
    voiceId: z.string().min(1),
  })
  .strict();

export type VoicePreferencePutRequest = z.infer<typeof voicePreferencePutRequestSchema>;

export const voicePreferenceDataSchema = z
  .object({
    voiceId: z.string().min(1),
  })
  .strict();

export type VoicePreferenceData = z.infer<typeof voicePreferenceDataSchema>;
export const voicePreferenceResponseSchema = apiEnvelope(voicePreferenceDataSchema);
export type VoicePreferenceResponse = z.infer<typeof voicePreferenceResponseSchema>;
